// src/client/services/paymentService.ts
import { ok, err, type ServiceResult, type Ngwee } from '@/lib/types';
import type { MobileMoneyProvider } from '@/constants/config';
import { supabase } from '@client/lib/supabase';
import type { PaymentFixture } from '@client/lib/fixtures';

export interface InitiateRepaymentInput {
  loanId: string;
  amount: Ngwee;
  provider: MobileMoneyProvider;
}

interface ProcessPaymentResponse {
  payment_id: string;
  loan_id: string;
  amount_ngwee: number;
  status: 'initiated' | 'pending' | 'completed' | 'failed' | 'timeout' | 'reconciled';
  provider: MobileMoneyProvider;
}

function newIdempotencyKey(): string {
  // Browsers: crypto.randomUUID is available on jsdom + Vite. Fallback (older
  // browsers, server-side renders) uses a millisecond-precision pseudo-random.
  // The Edge Function does the actual idempotency check, so collisions only
  // matter within a single user session.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pay-${Date.now()}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

export async function initiateRepayment(
  input: InitiateRepaymentInput,
): Promise<ServiceResult<PaymentFixture>> {
  if (!input.loanId.trim()) return err('invalid_loan_id');
  if (Number(input.amount) <= 0) return err('invalid_amount');

  const idempotencyKey = newIdempotencyKey();
  const { data, error } = await supabase.functions.invoke<ProcessPaymentResponse>('process-payment', {
    body: {
      loan_id: input.loanId,
      amount_ngwee: Number(input.amount),
      provider: input.provider,
      idempotency_key: idempotencyKey,
    },
  });

  if (error) {
    // FunctionsHttpError exposes the upstream status via .context.status.
    // Map to typed error codes for screens.
    const status = (error as { context?: { status?: number } }).context?.status ?? 0;
    if (status === 401) return err('unauthorized');
    if (status === 403) return err('forbidden');
    if (status === 404) return err('not_found');
    if (status === 409) return err('loan_not_repayable');
    if (status === 422) return err('amount_exceeds_outstanding');
    if (status === 502) return err('aggregator_failed');
    return err('internal_error');
  }
  if (!data) return err('internal_error');

  const fixture: PaymentFixture = {
    id: data.payment_id,
    loan_id: data.loan_id,
    amount_ngwee: data.amount_ngwee as Ngwee,
    status: data.status,
    provider: data.provider,
    reference: data.payment_id, // process-payment does not return a separate reference; reuse id
    created_at: new Date().toISOString(),
    completed_at: null,
  };
  return ok(fixture);
}

export async function pollPaymentStatus(paymentId: string): Promise<ServiceResult<PaymentFixture>> {
  if (!paymentId.trim()) return err('invalid_id');
  const { data, error } = await supabase
    .from('payments')
    .select('id, loan_id, amount_ngwee, status, provider, provider_ref, initiated_at, completed_at')
    .eq('id', paymentId)
    .maybeSingle();
  if (error) return err('internal_error');
  if (!data) return err('not_found');
  const fixture: PaymentFixture = {
    id: data.id as string,
    loan_id: (data.loan_id as string | null) ?? '',
    amount_ngwee: data.amount_ngwee as Ngwee,
    status: data.status as PaymentFixture['status'],
    provider: data.provider as PaymentFixture['provider'],
    reference: (data.provider_ref as string | null) ?? '',
    created_at: data.initiated_at as string,
    completed_at: (data.completed_at as string | null),
  };
  return ok(fixture);
}

/** Subscribe to real-time updates for a payment row. Returns an unsubscribe
 *  function — call it on component unmount to avoid channel leaks.
 *
 *  Supabase Realtime applies RLS: the borrower's JWT-authenticated client can
 *  only receive events for rows they own. The channel name includes the
 *  payment ID to prevent collisions with other active channels. */
export function subscribeToPaymentStatus(
  paymentId: string,
  cb: (status: PaymentFixture['status']) => void,
): () => void {
  const channel = supabase
    .channel(`payment:${paymentId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'payments',
        filter: `id=eq.${paymentId}`,
      },
      (payload: { new: { status: string } }) => {
        cb(payload.new.status as PaymentFixture['status']);
      },
    )
    .subscribe();

  return () => { void supabase.removeChannel(channel); };
}
